import {Injectable} from '@angular/core';
import {ProfessionPreview} from "../interfaces/profession-preview";
import {ProfessionDetails} from "../interfaces/profession-details";
import {ProfessionUserMatchingService} from "./profession-user-matching.service";
import {ImageService} from "../atoms/image/image.service";
import {ItemActionElement} from "../atoms/item-actions/item-action-element";
import {NotificationService} from "../atoms/notification/notification.service";
import {AppState} from "../store";
import {Store} from "@ngrx/store";
import {toggleFavorite} from "../store/actions/user.actions";
import {isEntityInFavorites} from "../store/selectors/user.selector";
import {take} from "rxjs";
import {OverlayService} from "../overlay/overlay.service";
import {ShareOverlayComponent} from "../shared/share-overlay/share-overlay.component";
import {CdColor} from "../enums/cd-color";
import {resetFiltersByEntity, setJobFilter} from "../store/actions/filter.actions";
import {StoryService} from "./story.service";
import {FavoriteEntityType} from "../enums/favorite-entity-type";
import {GlobalSearchResult} from "../search-filter/global-search-overlay/global-search-result";
import {GetGlobalSearchResults_globalSearch_jobs} from "../queries/__generated__/GetGlobalSearchResults";
import {Image} from "../interfaces/image";
import {GlobalSearchResultType} from "../enums/global-search-result-type";
import {JobRedirect} from "../interfaces/job-redirect";

@Injectable({
  providedIn: 'root'
})
export class ProfessionsService {

  private readonly PROFESSION_SINGLE_PAGE_ENDPOINT = "berufe";
  private readonly PROFESSIONS_OVERVIEW_PAGE_ENDPOINT = "berufe";
  public readonly PROFESSIONS_HASH_FILTER_PREFIX = "professionsFilter";
  private readonly ANCHOR_MAPPING = new Map<string, string>([
    ["lehrausbilding", "ausbildung"],
    ["lehre_und_matura", "ausbildung"],
    ["ausbildungsbetriebe", "bezirke"],
    ["aufgaben", "tätigkeiten"]
  ])
  constructor(
    private imageService: ImageService,
    private professionUserMatchingService: ProfessionUserMatchingService,
    private notifications: NotificationService,
    private store: Store<AppState>,
    private overlay: OverlayService,
    private storyService: StoryService,
  ) { }

  getProfessionUrlById(id: number): string {
    return `/${this.PROFESSION_SINGLE_PAGE_ENDPOINT}/${id}`;
  }

  getProfessionFullUrlById(id: number): string {
    return `${window.location.origin}/${this.PROFESSION_SINGLE_PAGE_ENDPOINT}/${id}`;
  }

  mapRawProfessionPreviewData(rawProfessionPreviewData: any): ProfessionPreview {
    let parsedProfessionPreview: ProfessionPreview = {
      id: parseInt(rawProfessionPreviewData.id),
      bicId: rawProfessionPreviewData.bicID ?? 0,
      name: rawProfessionPreviewData.name,
      requirements: [],
      matchingScore: 0,
      image: this.imageService.mapRawImageData(rawProfessionPreviewData.image),
      scholasticDuration: rawProfessionPreviewData.scholasticDuration ?? false,
      jobEducationalPaths: [],
      schoolSubjectIds: [],
      interestFields: rawProfessionPreviewData.interestFields ? rawProfessionPreviewData?.interestFields.map((el: any) => parseInt(el.id)) : [],
      selected: true,
      hasInsights: rawProfessionPreviewData?.jobLinkMappings?.length > 0 ?? false,
      startingSalary: parseInt(rawProfessionPreviewData.jobSalary?.entryValue) ?? 0,
      jobRedirects: [],
      jobWorkFields: [],
      searchTags: rawProfessionPreviewData.searchTags ?? []
    }

    if(rawProfessionPreviewData.jobWorkFields && rawProfessionPreviewData.jobWorkFields.length) {
      parsedProfessionPreview.jobWorkFields = rawProfessionPreviewData.jobWorkFields.map((el: any) => el.bicID);
    }

    rawProfessionPreviewData.requirementsByType?.requirementTypes?.forEach((requirementType: any) => {
      requirementType?.requirements?.forEach((requirement: any) => {
        parsedProfessionPreview.requirements.push(parseInt(requirement.bicID));
      });
    });

    rawProfessionPreviewData.jobEducationalPaths?.forEach((jobEducationalPath: any) => {
      parsedProfessionPreview.jobEducationalPaths.push({
        name: jobEducationalPath.name,
        id: parseInt(jobEducationalPath.id)
      });
    });

    rawProfessionPreviewData.schoolSubjectMappings?.forEach((schoolSubjectMapping: any) => {
      parsedProfessionPreview.schoolSubjectIds.push(parseInt(schoolSubjectMapping?.jobSchoolSubject?.id));
    });

    parsedProfessionPreview.matchingScore = this.professionUserMatchingService.getJobMatchingScore(parsedProfessionPreview);

    rawProfessionPreviewData.jobRedirects?.forEach((redirect: any) => {
      parsedProfessionPreview.jobRedirects.push({
        redirectType: redirect.redirectType,
        jobName: redirect.jobName,
        bicID: redirect.bicID
      });

    });

    return parsedProfessionPreview;

  }

  mapRawProfessionDetailsData(rawProfessionDetailsData: any): ProfessionDetails {
    let parsedProfessionDetails: ProfessionDetails = {
      id: parseInt(rawProfessionDetailsData.id),
      bicId: rawProfessionDetailsData.bicID ?? 0,
      name: rawProfessionDetailsData.name,
      requirements: [],
      matchingScore: 0,
      description: rawProfessionDetailsData.description ?? '',
      isScholasticProfession: false,
      searchTags: [],
      teaserImage: false,
      physicalSkills: [],
      hardSkills: [],
      socialSkills: [],
      selfSkills: [],
      otherSkills: [],
      methodologicalSkills: [],
      scholasticDuration: rawProfessionDetailsData.scholasticDuration ?? false,
      operationsAndTasks: rawProfessionDetailsData.operationsAndTasks ?? '',
      importantSchoolSubjects: [],
      bicTrainingProfessionalSchool: rawProfessionDetailsData.bicAusbildungBerufschule ?? '',
      image: rawProfessionDetailsData.image ?? null,
      jobEducationalPaths: [],
      startingSalary: parseInt(rawProfessionDetailsData.jobSalary?.entryValue) ?? 0,
      jobLinkMappings: [],
      schoolSubjectIds: [],
      expertTerms: [],
      bicPossibilities: rawProfessionDetailsData.bicMoeglichkeiten ?? "",
      bicAlternatives: rawProfessionDetailsData.bicAlternativen ?? "",
      selected: true,
      interestFields: rawProfessionDetailsData ? rawProfessionDetailsData.interestFields.map((el: any) => parseInt(el.id)) : [],
      hasInsights: rawProfessionDetailsData.jobLinkMappings.length > 0,
      relatedProfessionIds: [],
      jobRedirects: [],
      insights: [],
      heroVideo: rawProfessionDetailsData.videoUrl ?? "",
      jobSchools: [],
      pdfUrl: rawProfessionDetailsData.pdfUrl ?? "",
      jobWorkFields: [],

      // dummy
      salaryInfo: {
        amsUrl: "",
        lehrlingseinkommenUrl: "",
        begin: {from: 0, to: 0},
        apprentice: []
      },

      jobMarketDistricts: []
    }

    if(rawProfessionDetailsData.jobWorkFields && rawProfessionDetailsData.jobWorkFields.length) {
      parsedProfessionDetails.jobWorkFields = rawProfessionDetailsData.jobWorkFields.map((el: any) => el.bicID);
    }

    // Set this field to empty if actually empty result sent
    if(parsedProfessionDetails.bicTrainingProfessionalSchool.indexOf("redaktionell bearbeitet") !== -1) {
      parsedProfessionDetails.bicTrainingProfessionalSchool = "";
    }

    // Check if scholastic profession
    if(rawProfessionDetailsData.jobEducationalPaths?.map((e: any) => e.id).indexOf("1") > -1) {
      parsedProfessionDetails.isScholasticProfession = true;
    }

    //console.log(rawProfessionDetailsData.jobSalary);
    // map salary data
    if(rawProfessionDetailsData.jobSalary) {

      parsedProfessionDetails.salaryInfo.amsUrl=rawProfessionDetailsData.jobSalary.amsUrl;
      parsedProfessionDetails.salaryInfo.lehrlingseinkommenUrl=rawProfessionDetailsData.jobSalary.lehrlingseinkommenUrl;

      if(rawProfessionDetailsData.jobSalary.entries){
            rawProfessionDetailsData.jobSalary.entries.forEach((salary: any) => {
            if(salary.salaryType === "ENTRY") {
              parsedProfessionDetails.salaryInfo.begin.from = parseInt(salary.salaryFrom ?? '0');
              parsedProfessionDetails.salaryInfo.begin.to = parseInt(salary.salaryTo ?? '0');
            } else if(salary.salaryType.indexOf("YRO") !== -1) {
              const year = parseInt(salary.salaryType.slice(salary.salaryType.length - 1));
              parsedProfessionDetails.salaryInfo.apprentice[year - 1] = {
                  from: salary.salaryFrom,
                  to: salary.salaryTo
              };
            }
          })
      }

      // console.log('parsed details',parsedProfessionDetails);
    }

    // Clean Up Bic Alternatives
    parsedProfessionDetails.bicAlternatives = parsedProfessionDetails.bicAlternatives.replaceAll("°", "");
    parsedProfessionDetails.bicAlternatives = parsedProfessionDetails.bicAlternatives.replaceAll("#", "");

    // Map District Counts
    rawProfessionDetailsData.jobMarketDistricts?.districts?.forEach((rawJobMarketDistrict: any) => {
      let mappedDistrict = {
        districtId: rawJobMarketDistrict.districtID,
        trainingCompaniesCount: rawJobMarketDistrict.trainingCompanies?.length ?? 0,
        jobMarketEntriesCount: rawJobMarketDistrict.jobMarketEntries?.length ?? 0
      }

      parsedProfessionDetails.jobMarketDistricts.push(mappedDistrict);

    });

    rawProfessionDetailsData.requirementsByType?.requirementTypes?.forEach((requirementType: any) => {
      requirementType?.requirements?.forEach((requirement: any) => {
        parsedProfessionDetails.requirements.push(parseInt(requirement.bicID));
      });

      if(requirementType?.requirementType?.bicID) {
        switch (requirementType?.requirementType?.bicID) {
          case 2:
            parsedProfessionDetails.hardSkills = requirementType?.requirements?.map((requirement: any) => requirement.bicID);
            break;
          case 6:
            parsedProfessionDetails.methodologicalSkills = requirementType?.requirements?.map((requirement: any) => requirement.bicID);
            break;
          case 3:
            parsedProfessionDetails.socialSkills = requirementType?.requirements?.map((requirement: any) => requirement.bicID);
            break;
          case 4:
            parsedProfessionDetails.selfSkills = requirementType?.requirements?.map((requirement: any) => requirement.bicID);
            break;
          case 1:
            parsedProfessionDetails.physicalSkills = requirementType?.requirements?.map((requirement: any) => requirement.bicID);
            break;
        }
      }

    });

    rawProfessionDetailsData.schoolSubjectMappings?.forEach((schoolSubjectMapping: any) => {
      parsedProfessionDetails.importantSchoolSubjects.push({
        id: parseInt(schoolSubjectMapping?.jobSchoolSubject?.id),
        name: schoolSubjectMapping?.jobSchoolSubject?.subjectName,
        abbreviation: schoolSubjectMapping?.jobSchoolSubject?.subjectName,
      });
    });

    rawProfessionDetailsData.jobEducationalPaths?.forEach((jobEducationalPath: any) => {
      parsedProfessionDetails.jobEducationalPaths.push({
        name: jobEducationalPath.name,
        id: parseInt(jobEducationalPath.id)
      });
    });

    rawProfessionDetailsData.jobLinkMappings?.forEach((jobLinkMapping: any) => {
      parsedProfessionDetails.jobLinkMappings.push({
        category: parseInt(jobLinkMapping.jobLinkMappingCategory?.id),
        title: jobLinkMapping.jobLink?.titleFrontend,
        url: jobLinkMapping.jobLink?.url,
        type: parseInt(jobLinkMapping.jobLinkMappingType?.id),
      });
    });

    parsedProfessionDetails.matchingScore = this.professionUserMatchingService.getJobMatchingScore(parsedProfessionDetails);

    rawProfessionDetailsData.schoolSubjectMappings?.forEach((schoolSubjectMapping: any) => {
      parsedProfessionDetails.schoolSubjectIds.push(schoolSubjectMapping?.jobSchoolSubject?.id);
    });


    rawProfessionDetailsData.expertTerms?.forEach((expertTerm: any) => {
      parsedProfessionDetails.expertTerms.push({
        name: expertTerm.termName,
        description: expertTerm.description
      });
    });


    rawProfessionDetailsData.jobRedirects?.forEach((jobRedirect: any) => {
      parsedProfessionDetails.relatedProfessionIds.push(jobRedirect.bicID);

      let parsedJobRedirect = {
          bicID: jobRedirect.bicID,
          redirectType: jobRedirect.redirectType,
          jobName: jobRedirect.jobName
      }

      parsedProfessionDetails.jobRedirects.push(parsedJobRedirect);

    });

    rawProfessionDetailsData.insights?.forEach((rawInsight: any) => {
      parsedProfessionDetails.insights.push(this.storyService.mapRawStoryData(rawInsight));
    });

    rawProfessionDetailsData.jobSchools?.forEach((jobSchool: any) => {
      parsedProfessionDetails.jobSchools.push({
          name: jobSchool.school.name ?? "",
          url:  jobSchool.school.url ?? "",
          street: jobSchool.school.address.street ?? "",
          number: jobSchool.school.address.number ?? "",
          city: jobSchool.school.address.addressCity ?? ""
        });
    });

    return parsedProfessionDetails;

  }

  async generateMenuItems(profession: ProfessionPreview): Promise<ItemActionElement[]> {




    let foundJobRedirect: JobRedirect | null = null;
    if(profession.jobRedirects) {
      const redirects = profession.jobRedirects.filter((el) => el.redirectType === "REDIRECT");
      if(redirects.length > 0) {
        foundJobRedirect = redirects[0];
      }
    }

    let targetBicID = foundJobRedirect ? foundJobRedirect.bicID : profession.bicId;
    let targetName = foundJobRedirect ? foundJobRedirect.jobName : profession.name;

    const isFavorite = await new Promise<boolean>((resolve, reject) => {
      this.store.select(isEntityInFavorites(targetBicID)).pipe(take(1)).subscribe((value) => {
        resolve(value);
      });
    });

    let items: ItemActionElement[] = [
      {
        func: () => {
          this.store.dispatch(toggleFavorite({entityType: FavoriteEntityType.PROFESSION, id: targetBicID}));
          if(!isFavorite) {
            this.notifications.push("Gemerkt", CdColor.RIVER, "check-circle");
          } else {
            this.notifications.push("Aus Merkliste entfernt", CdColor.RIVER, "check-circle");
          }
        },
        title: isFavorite ? 'Gemerkt' : 'Merken',
        icon: isFavorite ? 'bookmark-full' : 'bookmark-outline',
        isActive: true
      },
      {
        func: () => {
          window.navigator.clipboard.writeText(this.getProfessionFullUrlById(targetBicID));
          this.notifications.push("Link kopiert", CdColor.RIVER, 'check-circle');
        },
        title: "Link kopieren",
        icon: "link",
        isActive: true
      },
      {
        func: () => {
          this.overlay.openWindowOverlay({componentMain: {component: ShareOverlayComponent, data: {url: this.getProfessionFullUrlById(targetBicID)}}, title: "Teilen " + targetName})
          // alert("Not implemented")
        },
        title: 'Teilen',
        icon: "share",
        isActive: true
      }
    ];

    return items;
  }

  getBaseOverviewUrl(): string {
    return this.PROFESSIONS_OVERVIEW_PAGE_ENDPOINT;
  }

  getOverviewUrlByPageIdx(idx: number): string {
    return `${this.getBaseOverviewUrl()}${idx === 1 ? '' : '/seite/' + idx}`;
  }

  /** Result looks like /berufe/page/3#asdsA234dfDa */
  /** Result looks like /berufe#asdsA234dfDa */
  /** Result looks like /berufe */
  /** Result looks like /berufe/page/3 */
  getFilterUrl(pageIdx: number, requirements: number[], searchTerm: string, schoolSubjects: number[], sortBy: string, withStories: boolean, educationPaths: number[], interestFields: number[]): string {
    let baseUrl = this.getOverviewUrlByPageIdx(pageIdx);
    let queryString = "";
    if(requirements.length) {
      queryString = `${queryString}selectedRequirements=${requirements.join(",")}&`;
    }

    if(searchTerm.length) {
      queryString = `${queryString}searchTermProfession=${searchTerm}&`;
    }

    if(schoolSubjects.length) {
      queryString = `${queryString}selectedSchoolSubjectIds=${schoolSubjects.join(",")}&`;
    }

    if(sortBy.length && sortBy !== "name-asc") { // like skip default sorting
      queryString = `${queryString}archiveSortBy=${sortBy}&`;
    }

    if(withStories) {
      queryString = `${queryString}professionsWithStories=1&`;
    }

    if(educationPaths.length) {
      queryString = `${queryString}educationPaths=${educationPaths.join(",")}&`;
    }

    if(interestFields.length) {
      queryString = `${queryString}selectedInterestFields=${interestFields.join(",")}&`;
    }

    if(queryString.length) {
      queryString = `${this.PROFESSIONS_HASH_FILTER_PREFIX}:${queryString}`;
      queryString = btoa(queryString);
      baseUrl = `${baseUrl}#${queryString}`;
    }

    return baseUrl;
  }

  setFiltersFromUrl(): void {

    /////////// Page number parsing is on component level now /////////
    // const hasPageNumber = window.location.pathname.indexOf("/page/") !== -1;
    //
    // if(hasPageNumber) {
    //   const splitPathName = window.location.pathname.split("/");
    //   let pageNumber = splitPathName[splitPathName.length - 1];
    //   let pageNumberParsed = parseInt(pageNumber);
    //   alert(pageNumberParsed);
    //   if(!isNaN(pageNumberParsed)) {
    //     this.store.dispatch(setJobFilter({key: 'professionsPageIdx', value: pageNumberParsed}))
    //   }
    // }

    let hash = window.location.hash.slice(1);
    if(hash.length) {
      this.store.dispatch(resetFiltersByEntity({key: 'allProfession'}));
      try {
        hash = atob(hash);
        const splitHash = hash.split(":");
        splitHash.shift();
        hash = splitHash.join("");
        const splitByFilters = hash.split("&");
        // console.log(splitByFilters);
        splitByFilters.forEach((filter) => {
          if(filter.length) {
            const splitFilter = filter.split("=");
            if(splitFilter.length === 2) {
              const filterName = splitFilter[0];
              switch (filterName) {
                // array integers type
                case "selectedRequirements": case "selectedSchoolSubjectIds": case "educationPaths": case "selectedInterestFields":
                  const splitValues = splitFilter[1].split(",");
                  this.store.dispatch(setJobFilter({key: filterName, value: splitValues.map((value) => parseInt(value))}));
                  break;
                // single string value type
                case "archiveSortBy": case "searchTermProfession":
                  console.warn(splitFilter[1]);
                  this.store.dispatch(setJobFilter({key: filterName, value: splitFilter[1]}));
                  break;
                // true/false type
                case "professionsWithStories":
                  const boolValue = splitFilter[1] !== '0';
                  this.store.dispatch(setJobFilter({key: filterName, value: boolValue}));
                  break;
              }
            }
          }

        })
      } catch (err: any) {
        console.error(err);
      }
    }
  }

  mapGlobalSearchResult(rawData: GetGlobalSearchResults_globalSearch_jobs): GlobalSearchResult {
    let hasApprenticeship = false;
    if(rawData.jobEducationalPaths && rawData.jobEducationalPaths.length) {
      // console.log(rawData.jobEducationalPaths);
      hasApprenticeship = rawData.jobEducationalPaths.filter(el => el.bicID === 1).length > 0;
    }
    let isGreen = false;
    if(rawData.jobWorkFields && rawData.jobWorkFields.length) {
      isGreen = rawData.jobWorkFields.filter(el => el.bicID === 22).length > 0;
    }
    let type: GlobalSearchResultType = (hasApprenticeship) ? GlobalSearchResultType.PROFESSION_APPRENTICESHIP : GlobalSearchResultType.PROFESSION;
    return {
      id: (rawData.bicID ?? -1).toString(),
      image: rawData.image as unknown as Image,
      quote: rawData.description ?? "",
      title: rawData.name ?? "",
      type: type,
      isGreen: isGreen
    }
  }

  getLocalAnchorFromExternalAnchor(anchor: string): string {
    const foundAnchor = this.ANCHOR_MAPPING.get(anchor);
    if(foundAnchor && foundAnchor.length) return foundAnchor;
    else return anchor;
  }

}
